// #region Imports
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import Guid from "_shared/guids/guid";
import { useMemo, useState } from "react";
import UserCoworkerQuery, {
  UserCoworkerFilterParameters,
  UserCoworkerOrderField,
  UserCoworkerOrderParameters,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import useInfiniteUserCoworkers from "users/information/coworker-information/_hooks/use-infinite-user-coworkers";
import NetworkConnectionQuery from "users/networking/connections/_api/queries/network-connection-query";
import useInfiniteNetworkConnections from "users/networking/connections/_hooks/use-infinite-network-connections";
import useSelectedNetworkConnections from "users/networking/connections/_hooks/use-selected-network-connections";
import CreateNetworkedGroupApiRequest from "users/networking/groups/_api/commands/create-networked-group-api-command";
import NetworkedGroupSelector from "users/networking/groups/_views/networked-group-selector";
// #endregion

// Every connection, in the server's order. Module scope keeps its identity, which the hook keys on.
const NETWORK_CONNECTION_QUERY = new NetworkConnectionQuery();

// #region Styled Components
const ContentContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    columnCount: 1,
  },
  [theme.breakpoints.up("md")]: {
    columnCount: 2,
  },
  [theme.breakpoints.up("lg")]: {
    columnCount: 3,
  },
  columnFill: "balance",
  columnGap: theme.spacing(4),
  gridColumn: "1 / auto",
  gridRowStart: "2",
  paddingTop: theme.spacing(0.75),
}));
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const StyledSubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: "50%",
}));
// #endregion

// #region Props
export interface CreateNetworkedGroupComponentProps {
  onCreate: (request: CreateNetworkedGroupApiRequest | null) => void;
}
// #endregion

// #region Component
export default function CreateNetworkedGroupComponent(props: Readonly<CreateNetworkedGroupComponentProps>) {
  const { onCreate } = props;

  // #region State
  const [groupName, setGroupName] = useState<string | undefined>();
  const [description, setDescription] = useState<string | undefined>();
  const [selectedMembers, setSelectedMembers] = useState<Guid[]>([]);

  // #region Queries
  const query = useMemo(
    () =>
      new UserCoworkerQuery(
        new UserCoworkerFilterParameters(),
        new UserCoworkerOrderParameters(UserCoworkerOrderField.Name, OrderDirection.Ascending),
        PaginationParameters.DEFAULT,
      ),
    [],
  );
  const {
    coworkers,
    isLoading: isLoadingCoworkers,
    error: coworkerError,
    loadMore: loadMoreCoworkers,
    retry: retryCoworkers,
  } = useInfiniteUserCoworkers(query);
  const {
    connections: networkConnections,
    isLoading: isLoadingNetworkConnections,
    error: networkConnectionError,
    loadMore: loadMoreNetworkConnections,
    retry: retryNetworkConnections,
  } = useInfiniteNetworkConnections(NETWORK_CONNECTION_QUERY);
  // A stability guarantee, not defensive memoization: the hook asks the server for each id it
  // cannot name, again whenever this array changes. A loaded coworker keeps the coworker name.
  const selectedNonCoworkerIds = useMemo(
    () => selectedMembers.filter((id) => !coworkers.data.some((coworker) => coworker.userId.isEqualTo(id))),
    [coworkers.data, selectedMembers],
  );
  const selectedConnections = useSelectedNetworkConnections(selectedNonCoworkerIds);

  // #endregion

  // #region Functions
  function generateDefaultGroupName(): string {
    if (selectedMembers.length === 0) return "New Group";
    const selectedMemberNames = selectedMembers.map((id) => {
      const coworker = coworkers.data.find((c) => c.userId.isEqualTo(id));
      if (coworker) return coworker.name.fullName;
      const selected = selectedConnections.find((person) => person.userId.isEqualTo(id));
      // A name that is still loading, or a person who is not visible, is not part of a group name.
      return selected?.state === "found" ? selected.name : undefined;
    }).filter((name): name is string => !!name);
    if (selectedMemberNames.length === 0) return "New Group";
    return `Chat with ${selectedMemberNames.join(", ")}`;
  }
  // #endregion

  // #region Render
  return (
    <div>
      <ContentContainer>
        <StyledTextField
          label="Group Name"
          variant="outlined"
        value={groupName ?? ""}
          onChange={(e) => setGroupName(e.target.value)}
        />
        <StyledTextField
          label="Description"
          variant="outlined"
          multiline
          rows={4}
        value={description ?? ""}
          onChange={(e) => setDescription(e.target.value)}
        />
        <NetworkedGroupSelector
          coworkers={coworkers.data}
          networkConnections={networkConnections.data}
          selectedMemberUserIds={selectedMembers}
          coworkerHasNextPage={coworkers.hasNextPage}
          isLoadingCoworkers={isLoadingCoworkers}
          coworkerError={coworkerError}
          onLoadMoreCoworkers={loadMoreCoworkers}
          onRetryCoworkers={retryCoworkers}
          networkHasNextPage={networkConnections.hasNextPage}
          isLoadingNetworkConnections={isLoadingNetworkConnections}
          networkConnectionError={networkConnectionError}
          onLoadMoreNetworkConnections={loadMoreNetworkConnections}
          onRetryNetworkConnections={retryNetworkConnections}
          onMemberSelected={(id: Guid) => {
            setSelectedMembers((prev) => {
              if (prev.some((memberId) => memberId.value === id.value)) {
                return prev.filter((memberId) => memberId.value !== id.value);
              }
              return [...prev, id];
            });
          }}
        />
      </ContentContainer>
      <StyledSubmitButton
        variant="contained"
        color="primary"
        disabled={selectedMembers.length === 0 || (groupName !== undefined && groupName.trim().length === 0)}
        onClick={() => {
          const request = new CreateNetworkedGroupApiRequest(
            groupName?.trim() || generateDefaultGroupName(),
            description?.trim() || undefined,
            selectedMembers
          );
          onCreate(request);
        }}
      >
        Create Group
      </StyledSubmitButton>
    </div>
  );
  // #endregion
}
